import { useHead } from '#app'
import { buildVideoObject } from '~/composables/seo/videoSchemaBuilder'

export interface VideoSeoInput {
    title?: string
    name?: string
    embedUrl?: string
    contentUrl?: string
    description?: string
    thumbnailUrl?: string
    uploadDate?: string
    duration?: string
    mainEntityOfPage?: string
    encodingFormat?: string
}

// Backward-compatible alias for existing callers.
export type YouTubeVideo = VideoSeoInput

function normalizeInput(video: VideoSeoInput): VideoSeoInput | null {
    const name = (video.name || video.title || '').trim()
    if (!name) return null

    return {
        ...video,
        name,
        description: (video.description || name).trim(),
    }
}

export function useVideoSeo(
    videos: VideoSeoInput[],
    options: {
        // Default to a single primary video per page to avoid diluted video-page signals.
        primaryOnly?: boolean
    } = {}
) {
    if (!Array.isArray(videos) || videos.length === 0) {
        return
    }

    const { primaryOnly = true } = options
    const normalizedVideos = videos
        .map(normalizeInput)
        .filter((v): v is NonNullable<typeof v> => v !== null)

    if (normalizedVideos.length === 0) {
        return
    }

    const videosToProcess = primaryOnly
        ? normalizedVideos.slice(0, 1)
        : normalizedVideos

    const videoObjects = videosToProcess
        .map(video =>
            buildVideoObject({
                name: video.name || '',
                description: video.description,
                contentUrl: video.contentUrl,
                embedUrl: video.embedUrl,
                thumbnailUrl: video.thumbnailUrl,
                uploadDate: video.uploadDate,
                duration: video.duration,
                mainEntityOfPage: video.mainEntityOfPage,
                encodingFormat: video.encodingFormat,
            })
        )
        .filter((obj): obj is NonNullable<typeof obj> => obj !== null)

    if (videoObjects.length === 0) {
        return
    }

    const structuredData =
        videoObjects.length === 1
            ? videoObjects[0]
            : {
                '@context': 'https://schema.org',
                '@graph': videoObjects,
            }

    useHead({
        script: [
            {
                key: 'video-seo-structured-data',
                type: 'application/ld+json',
                innerHTML: JSON.stringify(structuredData),
            },
        ],
    })
}

export function useSingleVideoSeo(video: VideoSeoInput) {
    useVideoSeo([video], { primaryOnly: true })
}
